import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { Container, Row } from 'react-bootstrap';
import { IconButton } from '@mui/material';
import { KeyboardArrowDown, Close } from '@mui/icons-material';
import "./ConsumerAction.scss";

export default function ConsumerAction(props) {
  const [fixedBasicInfoContainer, setFixedBasicInfoContainer] = useState(null);
  const [isMobileFormOpen, setIsMobileFormOpen] = useState(false);
  const basicInfoContainerRef = useRef();
  const formContainerRef = useRef();
  const fixedBasicInfoContainerRef = useRef(null);
  const isMobileFormOpenRef = useRef(false);
  const isMobileFormFocusRef = useRef(false);
  const scrollFrameRef = useRef(null);

  const setFixedBasicInfo = useCallback((nextValue) => {
    if (fixedBasicInfoContainerRef.current === nextValue) {
      return;
    }

    fixedBasicInfoContainerRef.current = nextValue;
    setFixedBasicInfoContainer(nextValue);
  }, []);

  const setMobileFormOpen = useCallback((nextValue) => {
    const formContainer = formContainerRef.current;

    if (!formContainer) {
      return;
    }

    if (isMobileFormOpenRef.current === nextValue && formContainer.classList.contains("open") === nextValue) {
      return;
    }

    // Keep DOM class and React state in sync without triggering duplicate renders.
    formContainer.classList.toggle("open", nextValue);
    isMobileFormOpenRef.current = nextValue;
    setIsMobileFormOpen(nextValue);
  }, []);

  const handleExpandForm = useCallback((forceOpen) => {
    const isCurrentlyOpen = formContainerRef.current?.classList.contains("open") || false;
    const shouldOpen = typeof forceOpen === "boolean" ? forceOpen : !isCurrentlyOpen;

    setMobileFormOpen(shouldOpen);
  }, [setMobileFormOpen]);

  useEffect(() => {
    const isMobileDevice = () => {
      return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.matchMedia("(max-width: 768px)").matches;
    };

    const isTargetWithinFormContainer = (target) => {
      const formContainer = formContainerRef.current;

      return !!(formContainer && target instanceof Node && formContainer.contains(target));
    };

    const handleFocusIn = (event) => {
      const isMobile = isMobileDevice();

      if (isMobile && isTargetWithinFormContainer(event.target)) {
        isMobileFormFocusRef.current = true;

        if (fixedBasicInfoContainerRef.current) {
          const formContainer = formContainerRef.current;
          if (formContainer && !formContainer.classList.contains("open") && !isMobileFormOpenRef.current) {
            setMobileFormOpen(true);
          }
        }
      }
    };

    const handleFocusOut = (event) => {
      const isMobile = isMobileDevice();

      if (isMobile && !isTargetWithinFormContainer(event.relatedTarget)) {
        isMobileFormFocusRef.current = false;
      }
    };

    const setFixed = () => {
      if (!basicInfoContainerRef.current) {
        return;
      }

      const heroOffset = {value: window.innerWidth < 768 ? 276 : 360};
      const scrollingElement = document.scrollingElement || document.documentElement;
      const windowScrollTop = (scrollingElement.scrollTop - heroOffset.value);
      const basicInfoContainerYPosition = basicInfoContainerRef.current.getBoundingClientRect().y;
      const isMobile = isMobileDevice();

      if ((windowScrollTop >= basicInfoContainerYPosition) && (isMobile && isMobileFormFocusRef.current)) {
        return;
      }

      if ((windowScrollTop >= basicInfoContainerYPosition) && !isMobileFormFocusRef.current) {
        setFixedBasicInfo(true);
      } else {
        setFixedBasicInfo(false);
        if (formContainerRef.current?.classList.contains("open")) {
          setMobileFormOpen(false);
        }
      }
    };

    const handleScroll = () => {
      if (scrollFrameRef.current) {
        return;
      }

      // Batch scroll layout reads to animation frames to keep input/focus handlers responsive.
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        setFixed();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, [setFixedBasicInfo, setMobileFormOpen]);

  const basicInfoForm = useMemo(
    () => cloneElement(props.basicInfo, { handleExpandForm, emailCapture: props.emailCapture }),
    [handleExpandForm, props.basicInfo, props.emailCapture]
  );

  return (
    <Fragment>
      <Row className="consumer-action-wrapper v3_1_1">
        <Container>
          <div className={`consumer-action-container ${fixedBasicInfoContainer ? "full" : ""}`}>
            <div className="consumer-action-about-wrapper">
              <div className="carrier-logo-list-container">
                <h2 className="fontPrimaryStyles">Get the best value <wbr/>from top-rated carriers.</h2>
                {props.carrierLogos}
              </div>
              <div className="about-container">
                {props.about}
              </div>
            </div>
            <div className="basic-info-wrapper">
              <div className={`basic-info-container ${fixedBasicInfoContainer ? "fixed-basic-info" : "basicInfoStyles"}`} ref={basicInfoContainerRef}>
                <div className="title-container">
                  <h2 className="fontStyles">Get Quotes In Seconds!</h2>
                </div>
                <div className="mobile-form-control-container">
                  <h2>Get Quotes In Seconds!</h2>
                  <IconButton
                    className="expand-form-control"
                    onClick={() => handleExpandForm()}
                    size="large">
                   {!isMobileFormOpen ? <KeyboardArrowDown /> : <Close/>}
                  </IconButton>
                </div>
                <div className={`form-container formStyles`} ref={formContainerRef}>
                  {basicInfoForm}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Row>
    </Fragment>
  );
}

ConsumerAction.propTypes = {
  carrierLogos: PropTypes.element,
  basicInfo: PropTypes.element,
  about: PropTypes.element,
  emailCapture: PropTypes.object,
};
